import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  BookOpen,
  Brain,
  MapPin,
  Phone,
  Clock,
  Zap,
  Shield,
  Target,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  Microscope,
  FlaskConical,
  Atom,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ─────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I found you while searching for Science tuition in Ghazipur. Please share Science course details. Thank you."
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "340+", label: "Students Enrolled", icon: Users },
  { value: "95%", label: "Board Success Rate", icon: TrendingUp },
  { value: "15+", label: "Expert Educators", icon: GraduationCap },
  { value: "3", label: "Science Subjects", icon: BookOpen },
];

const SCIENCE_SUBJECTS = [
  {
    subject: "Physics",
    icon: Atom,
    classes: "Class 9–12",
    topics: "Motion, Force, Light, Electricity, Magnetism, Modern Physics, Optics, Thermodynamics & more",
    color: "bg-blue-50 border-blue-200",
    iconColor: "text-blue-600",
  },
  {
    subject: "Chemistry",
    icon: FlaskConical,
    classes: "Class 9–12",
    topics: "Atoms, Reactions, Acids & Bases, Organic Chemistry, Chemical Bonding, Periodic Table, Electrochemistry & more",
    color: "bg-emerald-50 border-emerald-200",
    iconColor: "text-emerald-600",
  },
  {
    subject: "Biology",
    icon: Microscope,
    classes: "Class 9–12",
    topics: "Cell Biology, Life Processes, Human Physiology, Genetics, Evolution, Ecology, Reproduction & more",
    color: "bg-rose-50 border-rose-200",
    iconColor: "text-rose-600",
  },
  {
    subject: "General Science",
    icon: BookOpen,
    classes: "Class 8–10",
    topics: "Integrated Physics, Chemistry & Biology for Class 8, 9 and 10 — full NCERT + CBSE board coverage & more",
    color: "bg-amber-50 border-amber-200",
    iconColor: "text-amber-600",
  },
];

const WHY_SSS_SCIENCE = [
  {
    icon: Brain,
    title: "Concept-Based Science Teaching",
    desc: "Every Science concept is explained with real-world examples and diagrams — making Physics, Chemistry and Biology easy to understand and remember.",
  },
  {
    icon: FlaskConical,
    title: "Practical Demonstrations",
    desc: "Virtual and physical experiment demonstrations help students understand how Science works in real life, not just in theory.",
  },
  {
    icon: Zap,
    title: "Diagram & Numericals Mastery",
    desc: "Expert coaching on labelled diagrams for Biology and numerical problem-solving for Physics and Chemistry — key for scoring high in boards.",
  },
  {
    icon: Users,
    title: "Small Batches",
    desc: "Small batch sizes ensure every Science student gets individual attention, regular doubt-clearing and personalised feedback from faculty.",
  },
  {
    icon: Target,
    title: "Board-Pattern Question Practice",
    desc: "Chapter-wise questions aligned with CBSE board patterns, including 1-mark, 3-mark, 5-mark and case-study type questions.",
  },
  {
    icon: Shield,
    title: "Dedicated Doubt Sessions",
    desc: "Special doubt-clearing sessions for Science subjects are held regularly so no concept is left unclear before exams.",
  },
  {
    icon: BookOpen,
    title: "Full Study Materials Included",
    desc: "NCERT solutions, chapter notes, diagram sheets, previous-year papers and revision modules — all included in the course fee.",
  },
  {
    icon: TrendingUp,
    title: "Weekly Tests & Analytics",
    desc: "Regular chapter tests with detailed performance analytics help identify weak topics and track improvement systematically.",
  },
];

const TESTIMONIALS = [
  {
    name: "Neha Singh",
    cls: "Class 10, 2025",
    rating: 5,
    text: "SSS Achievers is the best Science tuition in Ghazipur. My daughter scored 94 in Science boards. The diagrams and experiment explanations made Biology so simple!",
  },
  {
    name: "Arjun Pandey",
    cls: "Class 12 PCM, 2025",
    rating: 5,
    text: "Physics numericals used to scare me. The faculty at SSS Achievers broke down every concept step by step. I scored 89 in Physics in my board exam!",
  },
  {
    name: "Sunita Devi (Parent)",
    cls: "Parent of Class 9 Student",
    rating: 5,
    text: "My son improved in all three Science subjects — Physics, Chemistry and Biology — after joining SSS Achievers. The small batch size and regular tests make a huge difference.",
  },
  {
    name: "Priti Gupta",
    cls: "Class 12 PCB, 2025",
    rating: 5,
    text: "Chemistry at SSS Achievers is taught so logically. Organic Chemistry became my strongest subject. Highly recommend SSS Achievers for Science coaching in Ghazipur!",
  },
];

const COMPARISON = [
  { feature: "Subject Coverage", sss: "Physics, Chemistry, Biology", others: "Often single-subject only" },
  { feature: "Batch Size", sss: "Small Batch (focused)", others: "50+ students (crowded)" },
  { feature: "Practical Demonstrations", sss: "Included in every lesson", others: "Rarely done" },
  { feature: "Diagram Practice", sss: "Regular Biology diagram drills", others: "Not emphasised" },
  { feature: "Numericals Practice", sss: "Daily Physics & Chemistry", others: "Occasional only" },
  { feature: "Mock Tests", sss: "Weekly chapter + board mocks", others: "Monthly or less" },
  { feature: "Demo Class", sss: "Free before enrollment", others: "Usually not offered" },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function ScienceTuitionGhazipur() {
  return (
    <>
      <Helmet>
        <title>Science Tuition in Ghazipur | Physics Chemistry Biology Coaching – SSS Achievers</title>
        <meta
          name="description"
          content="Best Science tuition in Ghazipur for Class 8–12. Expert Physics, Chemistry & Biology coaching at SSS Achievers. 95% success rate, small batches, free demo class. Enroll now!"
        />
        <meta
          name="keywords"
          content="science tuition in Ghazipur, science coaching Ghazipur, physics tuition Ghazipur, chemistry coaching Ghazipur, biology tuition Ghazipur, best science coaching Ghazipur UP, class 10 science tuition Ghazipur, SSS Achievers science, CBSE science coaching Ghazipur, PCB coaching Ghazipur"
        />
        <link rel="canonical" href="https://www.sssachievers.com/science-tuition-ghazipur" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Science Tuition in Ghazipur | Physics Chemistry Biology – SSS Achievers" />
        <meta property="og:description" content="Expert Science coaching in Ghazipur for Class 8–12. Physics, Chemistry & Biology by specialist faculty. 95% success rate, free demo class." />
        <meta property="og:url" content="https://www.sssachievers.com/science-tuition-ghazipur" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Science Tuition in Ghazipur | SSS Achievers" />
        <meta name="twitter:description" content="Expert Physics, Chemistry & Biology coaching in Ghazipur. 95% success rate, free demo class at SSS Achievers." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Best Science Tuition Ghazipur", "Physics Chemistry Biology Coaching Ghazipur"],
                "description": "SSS Achievers offers the best Science tuition in Ghazipur for Class 8–12 covering Physics, Chemistry and Biology with expert faculty and a 95% board success rate.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.5825,
                  "longitude": 83.5805
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Which is the best Science tuition in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is the top-rated Science tuition centre in Ghazipur. With specialist faculty for Physics, Chemistry and Biology, small batch sizes and a 95% CBSE board success rate, SSS Achievers is the #1 Science coaching institute in Ghazipur for Class 8–12."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers offer separate Physics, Chemistry and Biology coaching?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. SSS Achievers provides dedicated expert coaching for all three Science streams — Physics, Chemistry and Biology — each taught by specialist subject faculty. Students can enrol for all three or individual subjects as needed."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers teach Science for Class 10 board exam?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers provides comprehensive Class 10 Science coaching in Ghazipur covering all NCERT chapters, board-pattern question practice, diagram drilling, previous year papers and full mock tests to maximise board exam scores."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is there a free demo Science class at SSS Achievers Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers a completely free demo class for Science. You can experience the teaching style, meet the faculty and assess the classroom environment before enrolling."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the batch size for Science coaching at SSS Achievers?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Science batches at SSS Achievers are kept intentionally small to ensure each student gets individual attention, personalised guidance and regular doubt-clearing from the expert faculty."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Science Tuition in Ghazipur", "item": "https://www.sssachievers.com/science-tuition-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ══ HERO ══ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-[#0097b2] transition">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">Science Tuition in Ghazipur</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial="hidden" animate="visible" variants={fade} className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-amber-700">Ghazipur's Best Science Coaching Centre</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Best <span className="text-[#0097b2]">Science Tuition</span> in Ghazipur — Physics, Chemistry & Biology
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  SSS Achievers Ghazipur offers expert Science coaching for Class 8–12 with specialist faculty for Physics, Chemistry and Biology. Concept-based teaching, practical demonstrations and board-focused practice help students excel in every Science subject.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition">
                    Book Free Demo Science Class <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition">
                    <Phone className="w-4 h-4" /> Call: +91 99185 19991
                  </a>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  {["Free Demo Class", "Physics + Chemistry + Biology", "Small Batches", "95% Success Rate"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" /> {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="grid grid-cols-2 gap-4">
                {STATS.map(({ value, label, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition">
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-3xl font-bold text-[#0097b2]">{value}</span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">{label}</span>
                  </div>
                ))}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">Based on 340+ student reviews</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">Top Rated</p>
                    <p className="text-3xl font-bold">Science</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ SCIENCE SUBJECTS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                What We Teach at <span className="text-[#0097b2]">SSS Achievers Ghazipur</span> — Science
              </motion.h2>
              <p className="mt-3 text-slate-600">Expert specialist faculty for every Science stream — Physics, Chemistry &amp; Biology from Class 8 to 12</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SCIENCE_SUBJECTS.map(({ subject, icon: Icon, classes, topics, color, iconColor }, i) => (
                <motion.div key={subject} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className={`rounded-2xl border-2 ${color} bg-white p-6 hover:shadow-lg transition-all`}>
                  <div className={`w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${iconColor}`} />
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">{subject}</h3>
                  <p className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wide">{classes}</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{topics}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ WHY SSS FOR SCIENCE ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Why SSS Achievers is the <span className="text-[#0097b2]">Best Science Tuition</span> in Ghazipur
              </motion.h2>
              <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base">We make Science engaging, understandable and exam-ready for every student.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {WHY_SSS_SCIENCE.map(({ icon: Icon, title, desc }, i) => (
                <motion.div key={title} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white">
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ COMPARISON ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                SSS Achievers Science vs. Other Tuitions in Ghazipur
              </motion.h2>
            </div>
            <div className="overflow-hidden rounded-2xl border-2 border-gray-100 shadow-sm">
              <div className="grid grid-cols-3 bg-[#0097b2] text-white text-sm font-bold">
                <div className="px-5 py-4">Feature</div>
                <div className="px-5 py-4 text-center border-l border-white/20">SSS Achievers ✓</div>
                <div className="px-5 py-4 text-center border-l border-white/20 text-blue-100">Others</div>
              </div>
              {COMPARISON.map(({ feature, sss, others }, i) => (
                <div key={feature} className={`grid grid-cols-3 text-sm border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <div className="px-5 py-4 font-semibold text-slate-700">{feature}</div>
                  <div className="px-5 py-4 text-center border-l border-gray-100">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{sss}</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100 text-slate-400 text-xs">{others}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ TESTIMONIALS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                What Students Say About <span className="text-[#0097b2]">Science Tuition</span> at SSS Achievers
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TESTIMONIALS.map(({ name, cls, rating, text }, i) => (
                <motion.div key={name} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{cls}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ CAMPUS GLIMPSE ══ */}
        <section className="py-14 md:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-bold"
              >
                See Our <span className="text-[#0097b2]">Science Labs & Campus</span> in Ghazipur
              </motion.h2>
              <p className="mt-2 text-slate-500 text-sm">Modern classrooms, dedicated labs and proven results</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  src: "/images/SSS%20Achievers%20classroom.avif",
                  alt: "SSS Achievers Science coaching classroom in Ghazipur — AI-enabled smart classroom for Physics, Chemistry and Biology tuition for Class 8-12",
                  caption: "🏫 Science Classroom — SSS Achievers",
                },
                {
                  src: "/images/sss%20achievers%20building%20photo.avif",
                  alt: "SSS Achievers Science tuition institute campus in Ghazipur — Physics Chemistry Biology coaching centre at Badi Bagh, Uttar Pradesh",
                  caption: "🏢 Our Campus — Ghazipur, UP",
                },
                {
                  src: "/images/gallery/student-group.jpg",
                  alt: "SSS Achievers Science batch student group photo in Ghazipur — Physics, Chemistry and Biology students at the best Science tuition centre in Ghazipur",
                  caption: "👨‍🎓 Our Science Batch — SSS Achievers Ghazipur",
                },
              ].map(({ src, alt, caption }, i) => (
                <motion.figure
                  key={caption}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-md group"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="600"
                    height="224"
                  />
                  <figcaption className="bg-white px-4 py-3 text-sm font-semibold text-slate-700 border-t border-gray-100">
                    {caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        {/* ══ LOCATION + LINKS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Visit SSS Achievers for the Best <span className="text-[#0097b2]">Science Tuition in Ghazipur</span>
                </h2>
                <p className="text-slate-600 leading-relaxed">Centrally located in Ghazipur and accessible from all surrounding areas. Our AI-enabled classrooms and expert Science faculty await you.</p>
                <div className="space-y-3">
                  {[
                    { icon: MapPin, text: "96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001" },
                    { icon: Phone, text: "+91 99185 19991", href: `tel:${PHONE}` },
                    { icon: Clock, text: "Mon–Sat: 9:00 AM – 7:00 PM" },
                  ].map(({ icon: Icon, text, href }) => (
                    <div key={text} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-5 h-5 text-[#0097b2]" />
                      </div>
                      {href ? <a href={href} className="text-sm text-[#0097b2] font-semibold hover:underline mt-2">{text}</a> : <p className="text-sm text-slate-600 mt-2">{text}</p>}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/courses/class-9-foundation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="text-sm text-[#0097b2] font-semibold hover:underline flex items-center gap-1">Class 9 Science <ArrowRight className="w-3.5 h-3.5" /></Link>
                  <Link to="/courses/class-10-board-preparation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="text-sm text-[#0097b2] font-semibold hover:underline flex items-center gap-1">Class 10 Science <ArrowRight className="w-3.5 h-3.5" /></Link>
                  <Link to="/courses/class-11-foundation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="text-sm text-[#0097b2] font-semibold hover:underline flex items-center gap-1">Class 11 PCM/PCB <ArrowRight className="w-3.5 h-3.5" /></Link>
                  <Link to="/courses/class-12-board-preparation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="text-sm text-[#0097b2] font-semibold hover:underline flex items-center gap-1">Class 12 Boards <ArrowRight className="w-3.5 h-3.5" /></Link>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg">
                <div className="bg-[#0097b2] px-6 py-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">Find Us in Ghazipur</span>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SSS Achievers Science Tuition Ghazipur Location"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</motion.h2>
              <p className="mt-3 text-slate-500 text-sm">About Science tuition in Ghazipur at SSS Achievers</p>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Which is the best Science tuition in Ghazipur?", a: "SSS Achievers is the top-rated Science tuition centre in Ghazipur. With specialist faculty for Physics, Chemistry and Biology, small, focused batches and a 95% CBSE board success rate, SSS Achievers is Ghazipur's #1 Science coaching institute for Class 8–12." },
                { q: "Does SSS Achievers offer Physics, Chemistry and Biology coaching separately?", a: "Yes. SSS Achievers provides dedicated specialist coaching for Physics, Chemistry and Biology — each taught by subject experts. Students can join all subjects together or enrol individually as needed." },
                { q: "How does SSS Achievers help in CBSE Class 10 Science board preparation?", a: "SSS Achievers provides comprehensive Class 10 Science coaching covering all NCERT chapters, board-pattern question types, labelled diagram practice for Biology, numerical problem-solving for Physics and Chemistry, previous year papers and full mock tests." },
                { q: "Is there a free demo Science class at SSS Achievers?", a: "Yes — SSS Achievers offers a completely free demo class for Science. Attend a session to experience our teaching methodology and meet the faculty before making any commitment." },
                { q: "What study materials are provided for Science at SSS Achievers?", a: "All Science study materials at SSS Achievers are included in the course fee — chapter notes, diagram sheets, practice worksheets, NCERT solutions, previous year board papers and revision modules for Physics, Chemistry and Biology." },
                { q: "Does SSS Achievers provide Science coaching for Class 11 and 12 PCB/PCM?", a: "Yes. SSS Achievers provides expert coaching for Class 11 and 12 Science streams — both PCM (Physics, Chemistry, Maths) and PCB (Physics, Chemistry, Biology) — with board and entrance exam-focused preparation." },
              ]}
            />
          </div>
        </section>

        {/* ══ FINAL CTA ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <Star className="w-4 h-4 text-[#fcbf0a] fill-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">Ghazipur's #1 Science Tuition Centre</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Join the Best Science Tuition in Ghazipur</h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Physics, Chemistry & Biology — all under one roof. Limited seats available. Book your free demo class today at SSS Achievers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base">
                  Book Free Demo Science Class <ArrowRight className="w-5 h-5" />
                </a>
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base">
                  <Phone className="w-5 h-5" /> +91 99185 19991
                </a>
              </div>
              <p className="text-blue-200 text-xs mt-4">📍 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001</p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
